import { flow, getEnv, types } from 'mobx-state-tree';

import { createMapWithTransform } from 'utils/store/createMap';
import {
  ItemLoadingStateEnum,
  ListLoadingStateEnum
} from './LoadingStateEnums';
import ProjectAiModuleModel, {
  createProjectAiModuleModel,
  ProjectAiModuleModelType
} from './ProjectAiModuleModel';
import { StoreEnv } from './StoreEnv';

const ProjectAiModulesStore = types
  .model('ProjectAiModulesStore', {
    itemLoadingState: types.maybe(ItemLoadingStateEnum),
    listLoadingState: types.maybe(ListLoadingStateEnum),
    projectAiModules: types.maybe(types.map(ProjectAiModuleModel))
  })
  .actions((self) => {
    const upsertProjectAiModule = (data: any) => {
      if (!data) {
        return;
      }
      const module = createProjectAiModuleModel(data);
      if (!self.projectAiModules) {
        self.projectAiModules = types.map(ProjectAiModuleModel).create({});
      }
      self.projectAiModules.put(module);
    };

    const getProjectAiModules = flow(function* (
      organizationId: number,
      projectId: number
    ) {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        self.listLoadingState = 'loading';
        const result: any = yield client.getProjectAiModules(
          organizationId,
          projectId
        );

        const modules = result?.project_ai_modules || result;
        self.projectAiModules = createMapWithTransform(
          modules,
          createProjectAiModuleModel
        );
        self.listLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'ProjectAiModulesStore | getProjectAiModules',
            error,
            error.body
          );
        }

        if (client.isNotFound(error)) {
          self.listLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.listLoadingState = 'load_error';
        return undefined;
      }
    });

    const getProjectAiModule = flow(function* (
      organizationId: number,
      projectId: number,
      projectAiModuleId: number
    ) {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        self.itemLoadingState = 'loading';
        const result: any = yield client.getProjectAiModule(
          organizationId,
          projectId,
          projectAiModuleId
        );

        upsertProjectAiModule(result);
        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'ProjectAiModulesStore | getProjectAiModule',
            error,
            error.body
          );
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'load_error';
        return undefined;
      }
    });

    const generateAiModule = flow(function* (
      organizationId: number,
      projectId: number,
      aiModuleId: number,
      hypothesisIds: number[],
      additionalInput?: string,
      moduleTagId?: number
    ) {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        const result: any = yield client.generateAiModule(
          organizationId,
          projectId,
          aiModuleId,
          hypothesisIds,
          additionalInput,
          moduleTagId
        );

        // The API now returns immediately with { success, project_ai_module_id }.
        // The actual generation runs as a background job and updates arrive via ActionCable.
        return result;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'ProjectAiModulesStore | generateAiModule',
            error,
            error.body
          );
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'save_error';
        return undefined;
      }
    });

    const updateProjectAiModule = flow(function* (
      organizationId: number,
      projectId: number,
      projectAiModuleId: number,
      output: string
    ) {
      const { client, applicationStore } = getEnv<StoreEnv>(self);

      try {
        self.itemLoadingState = 'saving';
        const result: any = yield client.updateProjectAiModule(
          organizationId,
          projectId,
          projectAiModuleId,
          output
        );

        upsertProjectAiModule(result);
        self.itemLoadingState = undefined;
      } catch (error: any) {
        if (process.env.NODE_ENV !== 'production') {
          // tslint:disable-next-line
          console.error(
            'ProjectAiModulesStore | updateProjectAiModule',
            error,
            error.body
          );
        }

        if (client.isNotFound(error)) {
          self.itemLoadingState = 'not_found';
          return undefined;
        }

        if (applicationStore.handleAppError(error)) {
          return undefined;
        }

        self.itemLoadingState = 'save_error';
        return undefined;
      }
    });

    const handleProjectAiModuleUpdate = (data: any) => {
      upsertProjectAiModule(data);
    };

    return {
      getProjectAiModules,
      getProjectAiModule,
      generateAiModule,
      updateProjectAiModule,
      handleProjectAiModuleUpdate
    };
  })
  .views((self) => {
    return {
      get isListLoading(): boolean {
        return self.listLoadingState === 'loading';
      },
      get isListError(): boolean {
        return (
          self.listLoadingState === 'load_error' ||
          self.listLoadingState === 'access_denied' ||
          self.listLoadingState === 'feature_disabled'
        );
      },
      get isItemLoading(): boolean {
        return (
          self.itemLoadingState === 'loading' ||
          self.itemLoadingState === 'deleting'
        );
      },
      get isItemSaving(): boolean {
        return self.itemLoadingState === 'saving';
      },
      get isItemSaveError(): boolean {
        return (
          self.itemLoadingState === 'save_error' ||
          self.itemLoadingState === 'delete_error'
        );
      },
      get isItemLoadError(): boolean {
        return (
          self.itemLoadingState === 'load_error' ||
          self.itemLoadingState === 'access_denied' ||
          self.itemLoadingState === 'feature_disabled'
        );
      },
      get isAnyModuleGenerating(): boolean {
        if (!self.projectAiModules) {
          return false;
        }
        return Array.from(self.projectAiModules.values()).some(
          (pam) => pam.status === 'created' || pam.status === 'running'
        );
      },
      get generatingModules(): ProjectAiModuleModelType[] {
        if (!self.projectAiModules) {
          return [];
        }
        return Array.from(self.projectAiModules.values()).filter(
          (pam) => pam.status === 'created' || pam.status === 'running'
        );
      },
      get projectAiModuleList(): ProjectAiModuleModelType[] {
        if (!self.projectAiModules) {
          return [];
        }
        return Array.from(self.projectAiModules.values());
      },
      findByAiModuleId(
        aiModuleId: number
      ): ProjectAiModuleModelType | undefined {
        if (!self.projectAiModules) {
          return undefined;
        }
        return Array.from(self.projectAiModules.values()).find(
          (pam) => pam.ai_module?.id === aiModuleId
        );
      }
    };
  });

export type ProjectAiModulesStoreType = typeof ProjectAiModulesStore.Type;
export default ProjectAiModulesStore;
